import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Download, Trash2, Lock, HardDrive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PageHeader } from "./ui";

const CHUNK = 4 * 1024 * 1024;

function fmtBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function Vault() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(null); // {name, pct}
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const load = () => api.get("/vault/files").then(({ data }) => { setFiles(data.files); setTotal(data.total_bytes); });
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    try {
      setUploading({ name: file.name, pct: 0 });
      const { data } = await api.post("/vault/files", { name: file.name, size: file.size, content_type: file.type || "application/octet-stream" });
      const fid = data.file_id;
      const chunks = Math.max(1, Math.ceil(file.size / CHUNK));
      let n = 0;
      for (let o = 0; o < file.size || n === 0; o += CHUNK) {
        const buf = await file.slice(o, o + CHUNK).arrayBuffer();
        await api.put(`/vault/files/${fid}/chunk/${n}`, buf, { headers: { "Content-Type": "application/octet-stream" } });
        n++;
        setUploading({ name: file.name, pct: Math.round((n / chunks) * 100) });
        if (file.size === 0) break;
      }
      await api.post(`/vault/files/${fid}/complete`, { chunk_count: n, size: file.size });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const onPick = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };

  const download = async (f) => {
    try {
      const { data } = await api.get(`/vault/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: f.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const del = async (f) => {
    try { await api.delete(`/vault/files/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div>
      <PageHeader eyebrow="SECURE WORKSPACE" title="My Vault" subtitle="Encrypted file storage in your Vaultvec vault — no external cloud account needed">
        <Button data-testid="vault-upload-btn" onClick={() => inputRef.current?.click()} className="rounded-sm bg-primary hover:bg-primary/90 text-white glow-primary">
          <UploadCloud className="h-4 w-4 mr-1" strokeWidth={1.5} /> Upload file
        </Button>
        <input ref={inputRef} type="file" hidden onChange={onPick} data-testid="vault-file-input" />
      </PageHeader>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Files</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3">{files.length}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Stored</span><div className="font-heading text-3xl font-bold tracking-tighter mt-3">{fmtBytes(total)}</div></div>
          <div className="card-elevated p-6"><span className="text-xs text-muted-foreground uppercase tracking-widest">Storage</span><div className="font-mono text-sm mt-4 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[hsl(var(--chart-2))] glow-green" /> Built-in · no Azure needed</div></div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="vault-dropzone"
          className={`rounded-sm border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
        >
          {uploading ? (
            <div>
              <p className="text-sm">Uploading <span className="font-mono">{uploading.name}</span> — {uploading.pct}%</p>
              <div className="h-2 w-full max-w-md mx-auto bg-secondary rounded-full mt-3 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploading.pct}%` }} /></div>
            </div>
          ) : (
            <>
              <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
              <p className="text-sm mt-3">Drag &amp; drop a file here, or click to browse</p>
              <p className="text-[11px] text-muted-foreground mt-1">Any file type · stored in your private vault</p>
            </>
          )}
        </div>

        <div className="card-elevated rounded-sm overflow-hidden" data-testid="vault-files">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-widest">
              <th className="p-4 font-normal">Name</th><th className="p-4 font-normal">Size</th><th className="p-4 font-normal">Uploaded</th><th className="p-4 font-normal"></th>
            </tr></thead>
            <tbody>
              {files.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-muted-foreground text-sm">Your vault is empty. Upload your first file above.</td></tr>}
              {files.map((f) => (
                <tr key={f.id} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                  <td className="p-4 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" strokeWidth={1.5} /> {f.name}</td>
                  <td className="p-4 font-mono text-xs">{fmtBytes(f.size)}</td>
                  <td className="p-4 font-mono text-xs text-muted-foreground">{new Date(f.created_at).toLocaleString()}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="rounded-sm text-muted-foreground hover:text-foreground" data-testid={`vault-download-${f.id}`} onClick={() => download(f)}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-sm text-muted-foreground hover:text-primary" data-testid={`vault-delete-${f.id}`} onClick={() => del(f)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
